import React, { useState, useEffect } from "react";
import { db } from "../firebase.config";
import { Button, Box, useColorModeValue, Heading, Text, Badge, Flex, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Input, Stack, SimpleGrid } from "@chakra-ui/react";
import { getDoc, doc, getDocs, collection, updateDoc, query, where } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FadeLoader } from "react-spinners";
import { Helmet } from "react-helmet";
import ListingItem from "../components/listings/ListingItem";
import { ImLocation, ImPhone } from "react-icons/im";
import { FaWhatsapp, FaStore, FaBed, FaBath, FaRulerCombined, FaUserCircle, FaPlusCircle } from "react-icons/fa";
import { BsStarFill } from "react-icons/bs";
import moment from "moment";
import { Link } from "react-router-dom";

const SellerProfile = () => {
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newRanking, setNewRanking] = useState("");
  const [currentListingId, setCurrentListingId] = useState(null);
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchSellerProfileAndListings = async () => {
      setLoading(true);
      try {
        console.log("Fetching seller profile for ID:", sellerId);
        
        // Fetch seller profile from Featured-Agents collection
        const sellerDocRef = doc(db, "Featured-Agents", sellerId);
        const sellerDocSnap = await getDoc(sellerDocRef);

        if (sellerDocSnap.exists()) {
          const sellerData = sellerDocSnap.data();
          console.log("Seller found:", sellerData);
          setSeller(sellerData);
        } else {
          console.log("No seller found with ID:", sellerId);
          setError("No such seller found");
          setLoading(false);
          return;
        }

        // Fetch properties where agentId matches sellerId
        console.log("Fetching properties with agentId:", sellerId);
        const propertiesRef = collection(db, "properties");
        const q = query(propertiesRef, where("agentId", "==", sellerId));
        const propertiesSnapshot = await getDocs(q);
        
        let propertiesList = [];

        if (!propertiesSnapshot.empty) {
          console.log(`Found ${propertiesSnapshot.size} properties for this agent`);
          propertiesList = propertiesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        } else {
          console.log("No properties found with agentId:", sellerId);
          
          // Fallback: Try to get all properties and filter by agentId (for development)
          try {
            const allPropertiesRef = collection(db, "properties");
            const allPropertiesSnapshot = await getDocs(allPropertiesRef);
            const allProperties = allPropertiesSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            
            // Filter properties by agentId
            propertiesList = allProperties.filter(property => 
              property.agentId === sellerId
            );
            
            console.log(`Found ${propertiesList.length} properties after filtering all properties`);
          } catch (allPropertiesError) {
            console.log("Error fetching all properties:", allPropertiesError);
          }
        }

        console.log("Final properties list:", propertiesList);

        // Filter out any invalid properties
        const validProperties = propertiesList.filter(property => 
          property && typeof property === 'object'
        );

        // Sort by timestamp (newest first)
        validProperties.sort((a, b) => {
          const timeA = a.createdAt?.toDate?.() || new Date(0);
          const timeB = b.createdAt?.toDate?.() || new Date(0);
          return timeB - timeA;
        });

        setListings(validProperties);

      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching seller profile: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerProfileAndListings();
  }, [sellerId]);

  // Safe property access helper functions
  const getPropertyImage = (property) => {
    if (!property) return "/placeholder-property.jpg";
    return property.thumbnail || property.propertyImages?.[0] || "/placeholder-property.jpg";
  };

  const getPropertyTitle = (property) => {
    if (!property) return "Unknown Property";
    return property.title || property.propertyTitle || "Property Listing";
  };

  const getPropertyLocation = (property) => {
    if (!property) return "Location not specified";
    return property.location || "Location not specified";
  };

  const getPropertyPrice = (property) => {
    if (!property) return "N/A";
    if (property.price) {
      const price = Number(property.price);
      if (price >= 10000000) {
        return `PKR ${(price / 10000000).toFixed(1)}Cr`;
      }
      if (price >= 100000) {
        return `PKR ${(price / 100000).toFixed(1)}Lac`;
      }
      return `PKR ${price.toLocaleString()}`;
    }
    return "Price not specified";
  };

  const getPropertyType = (property) => {
    if (!property) return "Property";
    return property.propertyType || property.type || "Property";
  };

  const getTimestamp = (property) => {
    if (!property) return null;
    try {
      return property.createdAt?.toDate?.() || property.timestamp?.toDate?.() || null;
    } catch (error) {
      return null;
    }
  };

  const handleUpdateRanking = (listingId, currentRanking) => {
    if (!listingId) {
      toast.error("Invalid listing ID");
      return;
    }
    setCurrentListingId(listingId);
    setNewRanking(currentRanking || "");
    onOpen();
  };

  const handleConfirmUpdateRanking = async () => {
    if (!newRanking || isNaN(newRanking)) {
      toast.error("Please enter a valid ranking number");
      return;
    }

    if (!currentListingId) {
      toast.error("No listing selected");
      return;
    }

    try {
      const propertyDocRef = doc(db, "properties", currentListingId);
      await updateDoc(propertyDocRef, { 
        ranking: Number(newRanking),
        lastUpdated: new Date() 
      });
      
      toast.success("Ranking updated successfully");
      onClose();
      
      // Refresh listings
      const updatedListings = listings.map(listing => 
        listing && listing.id === currentListingId ? {...listing, ranking: Number(newRanking)} : listing
      ).filter(listing => listing !== undefined && listing !== null);
      
      // Re-sort after update
      updatedListings.sort((a, b) => {
        if (a.ranking !== undefined && b.ranking !== undefined) {
          return a.ranking - b.ranking;
        }
        const timeA = getTimestamp(a) || new Date(0);
        const timeB = getTimestamp(b) || new Date(0);
        return timeB - timeA;
      });
      
      setListings(updatedListings);
    } catch (error) {
      toast.error("Failed to update ranking: " + error.message);
    }
  };

  const checkDetails = (id) => {
    if (sellerId && id) {
      navigate(`/category/property/${id}?agentId=${encodeURIComponent(sellerId)}&type=featured-agent`, { 
        state: { type: "featured-agent" } 
      });
    } else {
      toast.error("Invalid agent or property ID");
    }
  };

  const handleSendToWhatsApp = (property) => {
    if (!property || !seller) {
      toast.error("Invalid property or seller information");
      return;
    }

    const phoneNumber = property.agentPhone || seller.phoneNumber;
    const formattedPhone = phoneNumber?.replace(/\D/g, "");
    
    if (!formattedPhone) {
      toast.error("Seller phone number not available");
      return;
    }

    const message = `*${getPropertyTitle(property)}*\n\n` +
      `*Price:* ${getPropertyPrice(property)}\n` +
      `*Location:* ${getPropertyLocation(property)}\n` +
      `*Type:* ${getPropertyType(property)}\n` +
      (property.bedrooms ? `*Bedrooms:* ${property.bedrooms}\n` : '') +
      (property.bathrooms ? `*Bathrooms:* ${property.bathrooms}\n` : '') +
      (property.area ? `*Area:* ${property.area} sq ft\n` : '') +
      `\nI'm interested in this property. Please provide more details.`;
    
    window.open(`https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <FadeLoader color="#10b981" />
      <Text ml={4} className="text-gray-600">Loading seller profile and properties...</Text>
    </div>
  );

  if (error) return (
    <Box className="text-center p-8">
      <Heading size="lg" className="text-red-500 mb-4">{error}</Heading>
      <Link to="/" className="text-emerald-600 hover:underline">Return to homepage</Link>
    </Box>
  );

  if (!seller) return (
    <Box className="text-center p-8">
      <Heading size="lg" className="text-gray-600 mb-4">Agent not found</Heading>
      <Link to="/" className="text-emerald-600 hover:underline">Browse other agents</Link>
    </Box>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <Helmet>
        <title>{`${seller.sellerName || seller.agentName} | Premium Property Agent | EstateGhar`}</title>
        <meta
          name="description"
          content={`Explore premium properties from ${seller.sellerName || seller.agentName}, a trusted property agent at EstateGhar.`}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Agent Profile Header with Thumbnail */}
        <Box className={`bg-${cardBg} rounded-xl p-6 mb-8 shadow-md border border-gray-100`}>
          <Flex direction={["column", "row"]} align="center" justify="space-between" gap={6}>
            <Flex align="center" gap={5} flex="1">
              {/* Agent Thumbnail Image */}
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-100 flex-shrink-0 shadow-md border-2 border-emerald-200">
                {seller.thumbnail || seller.avatar ? (
                  <img
                    src={seller.thumbnail || seller.avatar}
                    alt={seller.sellerName || seller.agentName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500">
                    <FaUserCircle className="text-white text-5xl" />
                  </div>
                )}
              </div>
              
              <Box flex="1">
                <Heading as="h1" size="xl" className="text-gray-800 mb-2">
                  {seller.sellerName || seller.agentName || "Unknown Agent"}
                </Heading>
                {seller.agentCompany && (
                  <Text className="text-gray-600 mb-2 font-medium">
                    {seller.agentCompany}
                  </Text>
                )}
                <Text className="text-gray-500 mb-3 flex items-center text-sm">
                  <ImLocation className="mr-2 text-emerald-500" size={14} />
                  {seller.address || "Address not specified"}
                </Text>
                <div className="flex flex-wrap gap-2">
                  <Badge bg="emerald-100" color="emerald-700" fontSize="sm" px={3} py={1} borderRadius="full">
                    Featured Agent
                  </Badge>
                  {seller.specialization && (
                    <Badge bg="teal-100" color="teal-700" fontSize="sm" px={2} py={1} borderRadius="full">
                      {seller.specialization}
                    </Badge>
                  )}
                </div>
              </Box>
            </Flex>
            
            <Box textAlign={["center", "right"]} className="flex flex-col items-center md:items-end gap-2">
           
            </Box>
          </Flex>
        </Box>

        {/* Listings Section Header with Add Property Button */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <Heading as="h2" size="lg" className="text-gray-800">
            Available Properties ({listings.length})
          </Heading>
          <Button
            onClick={() => navigate(`/add-property?agentId=${sellerId}`)}
            bg="emerald-600"
            _hover={{ bg: "emerald-700" }}
            color="white"
            leftIcon={<FaPlusCircle />}
            size="md"
            borderRadius="lg"
            className="shadow-md"
          >
            Add New Property
          </Button>
        </div>

        {listings.length === 0 ? (
          <Box className="text-center p-12 bg-white rounded-xl border border-gray-200">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaStore className="text-emerald-500 text-3xl" />
            </div>
            <Text fontSize="xl" className="text-gray-600 mb-4">
              No properties currently listed
            </Text>
            <Text fontSize="md" className="text-gray-500 mb-6">
              This agent hasn't listed any properties yet.
            </Text>
            <Button
              bg="emerald-600"
              _hover={{ bg: "emerald-700" }}
              color="white"
              onClick={() => navigate(`/add-property?agentId=${sellerId}`)}
            >
              Add Your First Property
            </Button>
          </Box>
        ) : (
          <SimpleGrid columns={[1, 1, 2, 3]} spacing={6}>
            {listings.map((property, index) => (
              <Box 
                key={property?.id || `property-${index}`}
                className={`bg-${cardBg} rounded-xl overflow-hidden border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg`}
                style={{ boxShadow: shadowColor }}
              >
                <div className="relative group overflow-hidden h-48 bg-gray-100">
                  <img
                    src={getPropertyImage(property)}
                    alt={getPropertyTitle(property)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.src = "/placeholder-property.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-black/50 backdrop-blur-sm">
                    <span className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full font-semibold shadow-lg transform group-hover:scale-105 transition-transform duration-300">
                      View Details
                    </span>
                  </div>
                  
                  <div className="absolute top-3 right-3 flex space-x-2">
                    {property.status === "sold" && (
                      <Badge bg="red.500" color="white" fontSize="xs" px={2} py={0.5} borderRadius="full">
                        Sold
                      </Badge>
                    )}
                    {property.featured && (
                      <Badge bg="purple.500" color="white" fontSize="xs" px={2} py={0.5} borderRadius="full">
                        Featured
                      </Badge>
                    )}
                    {property.offer && (
                      <Badge bg="orange.500" color="white" fontSize="xs" px={2} py={0.5} borderRadius="full">
                        Offer
                      </Badge>
                    )}
                  </div>
                  
                  <div className="absolute bottom-3 left-3">
                    <Badge bg="emerald.600" color="white" fontSize="xs" px={2} py={1} borderRadius="full">
                      {getPropertyType(property)}
                    </Badge>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Heading as="h3" size="sm" className="text-gray-800 line-clamp-2 font-semibold">
                      {getPropertyTitle(property)}
                    </Heading>
                    {getTimestamp(property) && (
                      <Badge bg="gray.100" color="gray.600" fontSize="xs" px={2} py={0.5} borderRadius="full">
                        {moment(getTimestamp(property)).fromNow()}
                      </Badge>
                    )}
                  </div>

                  <Text className="text-emerald-600 flex items-center mb-3 text-sm">
                    <ImLocation className="mr-1" size={12} />
                    {getPropertyLocation(property)}
                  </Text>

                  <Text className="text-lg font-bold text-emerald-600 mb-3">
                    {getPropertyPrice(property)}
                    {property.type === "rent" && (
                      <span className="text-xs font-normal text-gray-500 ml-0.5">/mo</span>
                    )}
                  </Text>

                  {/* Property Features */}
                  <div className="flex justify-between text-sm text-gray-500 mb-4">
                    {property.bedrooms > 0 && (
                      <div className="flex items-center gap-1">
                        <FaBed className="text-emerald-500" size={12} />
                        <span>{property.bedrooms} Bed</span>
                      </div>
                    )}
                    {property.bathrooms > 0 && (
                      <div className="flex items-center gap-1">
                        <FaBath className="text-emerald-500" size={12} />
                        <span>{property.bathrooms} Bath</span>
                      </div>
                    )}
                    {(property.area > 0 || property.marla > 0) && (
                      <div className="flex items-center gap-1">
                        <FaRulerCombined className="text-emerald-500" size={12} />
                        <span>{property.marla ? `${property.marla} Marla` : `${property.area} sq ft`}</span>
                      </div>
                    )}
                  </div>

                  <Stack direction="column" spacing={2}>
                    <Button
                      onClick={() => checkDetails(property.id)}
                      width="full"
                      bg="emerald.600"
                      color="white"
                      _hover={{
                        bg: "emerald.700",
                        transform: "translateY(-1px)",
                      }}
                      size="md"
                      borderRadius="lg"
                      className="shadow-sm"
                    >
                      View Details
                    </Button>
                    <Button
                      onClick={() => handleSendToWhatsApp(property)}
                      width="full"
                      bg="green.500"
                      _hover={{ bg: "green.600" }}
                      leftIcon={<FaWhatsapp />}
                      size="md"
                      borderRadius="lg"
                    >
                      Contact Agent
                    </Button>
                  </Stack>
                </div>
              </Box>
            ))}
          </SimpleGrid>
        )}

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </div>
  );
};

export default SellerProfile;